import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { supabase } from "../supabase/SupabaseClient";
import { useRealtime } from "../services/store";
import { fijar as fijarQuien, limpiar as limpiarQuien } from "../services/quien";
import { miCuentaDeBaja } from "../services/permisos";
import { olvidarResumen } from "../services/presidencia";
import { esAprobacionQr, tocaCorte } from "../shared/acceso";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [fullUser, setFullUser] = useState(null);
  const [loading, setLoading] = useState(true);
  /* LA CUENTA DADA DE BAJA (05/10). Se pregunta al hidratar, ANTES de apagar
   * `loading`, para que el armazón no llegue a dibujar ni un cuadro del hub:
   * con `deBaja` prendido, `PrivateRoute` muestra sólo el aviso. El corte de
   * verdad está en la base (ninguna llave abre) y en Supabase Auth (no puede
   * volver a entrar ni renovar el token); esto es lo que se ve. */
  const [deBaja, setDeBaja] = useState(false);
  const ubicaciones = useRealtime("ubicaciones");
  /* En un ref y no en el estado: el listener de `onAuthStateChange` se
   * registra una sola vez y se queda con el `user` del primer render —que es
   * `null` para siempre—, así que leerlo del estado haría que la guarda de
   * abajo no se active nunca. Es el bug clásico de la clausura vieja, y acá el
   * síntoma sería que "el arreglo no arregló nada". */
  const idActual = useRef(null);

  const hydrateUser = async (sessionUser, { forzar = false } = {}) => {
    /* NO SE REHIDRATA SI ES LA MISMA PERSONA, y ésta es la línea que corta el
     * bucle.
     *
     * `onAuthStateChange` no avisa sólo del login: también dispara
     * `TOKEN_REFRESHED` cada vez que se renueva el token. Rehidratando en cada
     * evento se rearmaba `fullUser` como un OBJETO NUEVO, y todo efecto que
     * dependiera de él volvía a correr — incluido el de las preferencias, que
     * pedía la sesión, que renovaba el token, que emitía `TOKEN_REFRESHED`.
     *
     * Los logs de auth lo muestran redondo: **32 refresh y 32 `/user` en 61
     * segundos**, hasta el 429 de Supabase. Ahí supabase-js no puede renovar,
     * cierra la sesión, y el hub te manda al login en menos de un minuto.
     *
     * `refrescarUsuario()` pasa `forzar` porque ahí sí se quiere volver a leer
     * la fila (se cambió la foto de perfil, por ejemplo). */
    if (!forzar && sessionUser?.id && idActual.current === sessionUser.id) {
      setUser(sessionUser);
      setLoading(false);
      return;
    }
    idActual.current = sessionUser?.id ?? null;
    setUser(sessionUser);

    if (!sessionUser) {
      limpiarQuien();
      setFullUser(null);
      setDeBaja(false);
      setLoading(false);
      return;
    }

    // En paralelo con el perfil: no suma una espera más al ingreso.
    const bajaPedida = miCuentaDeBaja();

    try {
      const { data: usuario, error } = await supabase
        .from("users")
        .select("*")
        .ilike("mail", sessionUser.email)
        .maybeSingle();

      setDeBaja(await bajaPedida);
      if (error) throw error;

      const fullSucursal = ubicaciones.find(
        (ub) => String(usuario?.sucursal || "") === String(ub.id),
      );

      const enriquecerUsuario = {
        ...usuario,
        nombreSucursal: `${fullSucursal?.nombre || ""}`,
        nombreCompleto: `${usuario?.apellido || ""}, ${usuario?.nombres || ""}`,
      };

      setFullUser(enriquecerUsuario);
      // Que el motor de escritura sepa quién está cargando. Se pasa el nombre
      // ya armado: lo que se guarda en cada fila son dos strings, no el perfil.
      fijarQuien(sessionUser.email,
                 `${usuario?.nombres || ""} ${usuario?.apellido || ""}`.trim());
    } catch (err) {
      setDeBaja(await bajaPedida);
      console.error("[Error] Al traer datos del usuario.", err);
      // Sin perfil se guarda igual el mail: es lo que no cambia, y con eso
      // alcanza para saber quién fue.
      fijarQuien(sessionUser.email, "");
      setFullUser(null);
    } finally {
      setLoading(false);
    }
  };

  // re-trae la fila de `users` del usuario actual -- se usa después de
  // subir una foto de perfil nueva, para que se vea sin recargar.
  const refrescarUsuario = () => hydrateUser(user, { forzar: true });

  useEffect(() => {
    let active = true;

    const hydrateUserMontaje = async (sessionUser) => {
      if (!active) return;
      await hydrateUser(sessionUser);
    };

    // sesión actual al montar (recarga de página, etc.)
    supabase.auth.getSession().then(({ data: { session } }) => {
      hydrateUserMontaje(session?.user ?? null);
    });

    // cambios de sesión (login, logout, refresh de token)
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        /* LA CONTRASEÑA NO ES LA ÚNICA FORMA DE EMPEZAR UNA SESIÓN, y hasta
         * ahora `isLogged` sólo lo escribía `login()`. El enlace de
         * «recuperar contraseña» crea la sesión del lado de Supabase
         * (`/verify`) sin pasar por acá, así que esta pestaña quedaba con una
         * sesión válida y sin la marca — y el efecto de abajo la cerraba.
         *
         * Se marca acá, que es el único lugar por el que pasan TODAS: la
         * contraseña, el enlace del mail y el refresco del token. */
        if (session) sessionStorage.setItem("isLogged", "true");
        hydrateUserMontaje(session?.user ?? null);
      },
    );

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* CERRAR SESIÓN AL CERRAR LA VENTANA — y no al volver de un enlace del mail.
   *
   * `isLogged` vive en `sessionStorage`, que es por pestaña: una pestaña que
   * nunca se autenticó no hereda la sesión guardada en el navegador. Ésa es la
   * intención y se mantiene.
   *
   * LO QUE ROMPÍA: el chequeo corría al montar, ANTES de que el cliente
   * terminara de procesar el `#access_token` que trae el enlace de recuperar
   * contraseña. Sin la marca, cerraba una sesión que se acababa de crear.
   *
   * Lo que se veía del otro lado es peor que un error: el enlace abría
   * `/crear-clave`, la sesión moría sola dos segundos después, y la pantalla
   * decía «este enlace ya se usó o venció». El token SÍ se había usado —lo
   * consumió `/verify`— así que pedir uno nuevo repetía el mismo final, para
   * siempre. Está en los logs de auth: `/verify` 303 con login OK a las
   * 14:15:42 y `/logout` a las 14:15:44.
   *
   * Dos cambios y los dos hacen falta:
   *
   *   · la marca se escribe en `onAuthStateChange`, por donde pasan TODAS las
   *     sesiones y no sólo la de la contraseña;
   *   · el chequeo espera a `getSession()`, que en supabase-js resuelve recién
   *     después de procesar la URL. Corriendo antes, la marca todavía no está
   *     escrita y volveríamos a lo mismo.
   *
   * Y SIN SESIÓN NO SE CIERRA NADA: `logout()` con la sesión ya vacía sólo
   * dispara un `signOut` de más contra Supabase en cada carga de la pantalla
   * de login.
   */
  useEffect(() => {
    let vivo = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!vivo || !session) return;
      /* EL CORTE DEL INGRESO CON QR (Igna, 02/11): las compus que ya estaban
       * logueadas salen UNA vez, para que vean la pantalla nueva con el QR.
       * Los celulares no. Se mira cuándo se abrió la sesión: la que
       * se abre después ya no se corta. Ver `shared/acceso`. */
      if (tocaCorte(session)) {
        logout();
        return;
      }
      if (sessionStorage.getItem("isLogged")) return;
      /* La aprobación del QR se abre en una pestaña nueva (la abre la
       * cámara): cerrarle la sesión ahí haría imposible aprobar. */
      if (esAprobacionQr(window.location.pathname)) {
        sessionStorage.setItem("isLogged", "true");
        return;
      }
      logout();
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* NO CERRAMOS SESIÓN POR INACTIVIDAD.
   *
   * Antes había un timer que deslogueaba a los 30 min sin mouse/teclado. Para un
   * hub interno que la gente deja abierto todo el día era una molestia: te
   * sacaba al login mientras estabas en otra pestaña o volviendo de un café, y
   * lo vivían como "el sistema me echa cada tanto" (pedido de José, 22/09).
   *
   * La sesión se mantiene sola: supabase-js renueva el token de acceso en
   * segundo plano (autoRefreshToken, prendido por defecto), así que un logueado
   * sigue logueado. Sigue cerrando al cerrar la ventana (la marca `isLogged` es
   * por pestaña), que es la única expiración que queda y es la que se quiere.
   *
   * Si algún día hace falta un corte por inactividad para una terminal
   * compartida, que sea explícito y largo, y ojalá sólo en esa terminal. */

  const login = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    sessionStorage.setItem("isLogged", "true");
  };

  const logout = async () => {
    sessionStorage.removeItem("isLogged");
    olvidarResumen();
    setFullUser(null);
    setUser(null);
    setDeBaja(false);
    idActual.current = null;
    /* `scope: "local"` — CIERRA ESTE NAVEGADOR Y NO TODOS.
     *
     * Por defecto `signOut()` es GLOBAL: revoca todos los refresh token de esa
     * persona, en todos sus dispositivos. Y arriba hay un efecto que llama a
     * `logout()` al montar cuando no encuentra `isLogged` en el
     * `sessionStorage` —que es por pestaña—, así que **abrir el hub en una
     * pestaña nueva o en el teléfono cerraba la sesión de la computadora**.
     * Lo que se ve del otro lado es una sesión que se cae sola y sin motivo.
     *
     * Cerrar sesión significa "salí de acá", no "sacame de todos lados". */
    await supabase.auth.signOut({ scope: "local" });
  };

  // Primera vez de cada persona / ya tiene clave / no le corresponde entrar.
  // El dato vive en auth.users, que el navegador no puede leer, así que lo
  // resuelve una función de la base que devuelve una sola palabra y nada más
  // (ver la migración estado_de_acceso).
  //
  // Si la consulta falla se devuelve "existente": ante la duda, mostrar el
  // campo de contraseña. Es el camino que sirve para los 15 de 16 que ya la
  // tengan, y el que menos confunde — decirle "es tu primera vez" a alguien
  // que ya entró cien veces es peor que pedirle la clave a un primerizo, que
  // tiene el link de "creá tu clave" ahí mismo.
  const consultarAcceso = async (email) => {
    const { data, error } = await supabase.rpc("estado_de_acceso", {
      p_mail: email,
    });
    if (error) {
      console.warn("[auth] no se pudo consultar el estado de acceso:", error);
      return "existente";
    }
    return data;
  };

  // Para cuando `estado_de_acceso` dice sin_cuenta: la lista de accesos habilita
  // el dominio entero, así que un punto de menos en el nombre llega hasta acá
  // disfrazado de alta pendiente. Devuelve la cuenta parecida o null.
  const buscarCorreoParecido = async (email) => {
    const { data, error } = await supabase.rpc("correo_parecido", {
      p_mail: email,
    });
    // Si falla, se sigue como antes. Es una ayuda, no un requisito para entrar.
    if (error) {
      console.warn("[auth] no se pudo buscar un correo parecido:", error);
      return null;
    }
    return data || null;
  };

  // primer ingreso de cada usuario migrado (no tiene password conocida):
  // dispara el mail de "recuperar contraseña" de Supabase para que la
  // defina por su cuenta.
  //
  // OJO con redirectTo: apuntaba a /reset-password, que NO es una ruta de esta
  // app. El mail llegaba bien y el link terminaba en una pantalla en blanco,
  // así que el flujo entero estaba muerto sin que se notara.
  const recuperarPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/crear-clave`,
    });
    if (error) throw error;
  };

  // cambia la password del usuario logueado y, si venía con la password
  // temporal, apaga la bandera que obliga a cambiarla
  const cambiarPassword = async (nuevaPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: nuevaPassword,
      data: { must_reset_password: false },
    });
    if (error) throw error;
  };

  const mustResetPassword = !!user?.user_metadata?.must_reset_password;

  return (
    <AuthContext.Provider
      value={{
        user,
        fullUser,
        login,
        logout,
        consultarAcceso,
        buscarCorreoParecido,
        recuperarPassword,
        cambiarPassword,
        mustResetPassword,
        deBaja,
        loading,
        isAuthenticated: !!user,
        refrescarUsuario,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error("[Error] uso del authContext fuera del authProvider ✘");

  return context;
};
